'use client';

/**
 * DataTable — generic sortable table primitive (spec 093).
 *
 * Compound component with publicly composable sub-components:
 *   <DataTable>
 *     <DataTable.Header>
 *       <DataTable.RankColumn />
 *       <DataTable.IdentityColumn header="Trader" />
 *       <DataTable.SortableColumn sortKey="pnl" header="P&L" align="right" />
 *       <DataTable.Column header="Volume" align="right" />
 *     </DataTable.Header>
 *     <DataTable.Body>
 *       {(entry, i) => (
 *         <DataTable.Row isCurrentUser={...} onClick={...}>
 *           <DataTable.RankCell value={entry.rank} />
 *           <DataTable.IdentityCell user={entry.user} />
 *           <DataTable.NumericCell value={entry.pnl} format="usdSigned" tone="auto" />
 *           <DataTable.NumericCell value={entry.volume} format="usd" hideOnMobile />
 *         </DataTable.Row>
 *       )}
 *     </DataTable.Body>
 *   </DataTable>
 *
 * Owns: layout (desktop table + mobile card stack), sort indicator chevron motion,
 *       pagination controls, anchor-scroll pulse, keyboard a11y, color tone
 *       resolution, threshold-icon flip animation, tabular-nums enforcement.
 *
 * Does NOT own: data fetching, sort state, URL sync, cursor decoding. All those
 *               concerns stay with callers; primitive is fully controlled.
 *
 * See: frontend/specs/093-data-table-primitive/.
 */
import { c as _c } from "react/compiler-runtime";
import type { IntentHandlers } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import { Children, type ComponentProps, type ComponentType, type Context, createContext, isValidElement, type KeyboardEvent, type MouseEvent, type ReactNode, type RefObject, use, useEffect, useRef } from 'react';
import type { UserAvatarSize } from '@/components/images/user-avatar';
import { PNL_DUST_D6, pnlColorClass } from '@/components/portfolio/portfolio-helpers';
import { UserIdentity, type UserIdentityShape } from '@/components/profile/user-identity';
import { RankBadge } from '@/components/social/shared/rank-badge';
import { rankCellNumeralClassName } from '@/components/social/shared/rank-numeral';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Badge } from '@/components/ui/app-badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/app-table';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon, ArrowRight01Icon, ArrowUp01IconSolid, ArrowUpRight01Icon, CircleArrowDown02IconSolid } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { D6_SCALE } from '@/config/solana';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatCountFor } from '@/lib/format/format-count';
import { EASE_DEFAULT_MOTION, EASE_EXIT_MOTION, EASE_POSITION_MOTION, MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { FEED_CARD_HOVER_BACKGROUND, FEED_CARD_OUTLINE_INTERACTION, FEED_CARD_RESTING_OUTLINE } from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants & motion variants (migrated verbatim from the legacy
// LeaderboardTable so visual behaviour is byte-identical for the three
// social tabs).
// ---------------------------------------------------------------------------

const LEADERBOARD_SORT_ENTRANCE = {
  duration: 0.1,
  ease: EASE_DEFAULT_MOTION
} as const;
const LEADERBOARD_SORT_EXIT = {
  duration: 0.075,
  ease: EASE_EXIT_MOTION
} as const;
const LEADERBOARD_SORT_MICRO = {
  duration: 0.085,
  ease: EASE_POSITION_MOTION
} as const;
type DataTableCardUntil = 'md' | 'lg' | 'xl';
function dataTableDesktopMedia(cardUntil: DataTableCardUntil): string {
  switch (cardUntil) {
    case 'md':
      return '(min-width: 768px)';
    case 'lg':
      return '(min-width: 1024px)';
    case 'xl':
      return '(min-width: 1280px)';
    default:
      {
        const _exhaustive: never = cardUntil;
        return _exhaustive;
      }
  }
}
function dataTableLayoutClassNames(cardUntil: DataTableCardUntil): {
  desktop: string;
  mobile: string;
} {
  switch (cardUntil) {
    case 'md':
      return {
        desktop: 'hidden md:block',
        mobile: 'flex flex-col gap-2 md:hidden'
      };
    case 'lg':
      return {
        desktop: 'hidden lg:block',
        mobile: 'flex flex-col gap-2 lg:hidden'
      };
    case 'xl':
      return {
        desktop: 'hidden xl:block',
        mobile: 'flex flex-col gap-2 xl:hidden'
      };
    default:
      {
        const _exhaustive: never = cardUntil;
        return _exhaustive;
      }
  }
}
const SORT_HEADER_ICON_BOX = 'inline-flex size-3 shrink-0 origin-center items-center justify-center';
const SORT_ICON_OFFSET_Y_PX = 10;
const SORT_INDICATOR_VARIANTS = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      opacity: LEADERBOARD_SORT_ENTRANCE,
      y: LEADERBOARD_SORT_MICRO
    }
  },
  hidden: {
    opacity: 0,
    y: SORT_ICON_OFFSET_Y_PX,
    transition: {
      opacity: LEADERBOARD_SORT_EXIT,
      y: LEADERBOARD_SORT_MICRO
    }
  }
} as const;

/** Inset tables (market detail): room for rank + optional Δ-rank inline. */
const COL_RANK_CLASS_INSET = 'min-w-0 shrink-0 w-12 text-center' as const;
/** Feed chrome (social leaderboard): ~half the inset rank footprint; trophy + 2-digit rank still fit. */
const COL_RANK_CLASS_FEED = 'min-w-0 shrink-0 w-8 text-center' as const;
/** Feed chrome (full-width social leaderboard): identity fixed at 262px so a wide
 *  table doesn't stretch the name column and leave a gulf before the numeric
 *  columns. */
const COL_IDENTITY_CLASS_FEED = 'min-w-0 w-[262px] max-w-[262px]'; // ARBITRARY: identity column fixed at 262px so avatar + name + handle fit on a single line without truncation, matching legacy leaderboard layout.
/** Inset chrome (market-detail tables, narrow column): identity is the sole
 *  auto-width column so the `table-fixed` layout sends ALL slack here instead of
 *  proportionally inflating the fixed rank/numeric columns. Below `xl` the
 *  Volume/Unrealized/Realized columns are hidden, leaving large slack — without
 *  this the 48px rank column ballooned to ~85px and centered its trophy, opening
 *  a weird gap before the avatar. */
const COL_IDENTITY_CLASS_INSET = 'min-w-0';
function identityColClass(isFeed: boolean): string {
  return isFeed ? COL_IDENTITY_CLASS_FEED : COL_IDENTITY_CLASS_INSET;
}

/** Desktop `<tr>` when `desktopRowChrome="feed"` — segmented outer border + card corners (launchpad feed row parity). */
const FEED_DESKTOP_ROW_OUTLINE_CLASS = '[&>td]:border-solid [&>td]:border-border-primary [&>td]:border-t [&>td]:border-b [&>td:first-child]:border-l [&>td:last-child]:border-r [&>td:first-child]:rounded-tl-[var(--radius-card)] [&>td:first-child]:rounded-bl-[var(--radius-card)] [&>td:last-child]:rounded-tr-[var(--radius-card)] [&>td:last-child]:rounded-br-[var(--radius-card)]' as const;

// ---------------------------------------------------------------------------
// Internal contexts (NOT exported)
// ---------------------------------------------------------------------------

type DesktopRowChrome = 'inset' | 'feed';
const DesktopRowChromeContext: Context<DesktopRowChrome> = createContext<DesktopRowChrome>('inset');
function useDesktopRowChrome(): DesktopRowChrome {
  return use(DesktopRowChromeContext);
}
function useDesktopCellPad() {
  return useDesktopRowChrome() === "feed" ? "px-4 py-4" : "px-2 py-3";
}
interface SortContextValue {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (sort: string) => void;
  onSortOrderChange?: ((order: 'asc' | 'desc') => void) | undefined;
  prefersReducedMotion: boolean | null;
}
const SortContext: Context<SortContextValue | null> = createContext<SortContextValue | null>(null);
function useSortContext(): SortContextValue {
  const ctx = use(SortContext);
  if (ctx === null) {
    throw new Error('DataTable.* sub-components must be used inside <DataTable>');
  }
  return ctx;
}
type Layout = 'desktop' | 'mobile';
const LayoutContext: Context<Layout> = createContext<Layout>('desktop');
function useLayout(): Layout {
  return use(LayoutContext);
}

/**
 * When a desktop `<DataTable.Row href=…>` is rendered, the row provides this so
 * the first cell (`DataTable.RankCell`) can paint a stretched `<Link>` overlay.
 * A `<tr>` may not be an `<a>` and may not directly contain one, so the anchor
 * lives inside the rank `<td>` positioned `absolute inset-0` against the
 * row's containing block — giving a real anchor (viewport RSC prefetch, middle-click,
 * open-in-new-tab, soft nav) that covers the whole row. Intent handlers ride on
 * the overlay (not the `<tr>`) because the overlay captures the row's pointer
 * events. `null` on mobile / non-href rows → no overlay.
 */
interface RowLinkValue {
  href: string;
  ariaLabel?: string | undefined;
  onMouseEnter?: (() => void) | undefined;
  onFocus?: (() => void) | undefined;
  onPointerDown?: (() => void) | undefined;
}
const RowLinkContext: Context<RowLinkValue | null> = createContext<RowLinkValue | null>(null);

// ---------------------------------------------------------------------------
// DataTable root
// ---------------------------------------------------------------------------

export interface DataTableProps<TEntry, TSort extends string> {
  /** Row data — already ordered by the caller. The primitive does NOT re-sort. */
  entries: ReadonlyArray<TEntry>;
  /** Currently active sort column. */
  sortBy: TSort;
  /** Currently active sort direction. */
  sortOrder: 'asc' | 'desc';
  /** Fired when user clicks a sortable header for a NEW column. */
  onSortChange: (sort: TSort) => void;
  /**
   * Fired when user clicks the current column to toggle direction. Omit for a
   * fixed-order table: inactive columns change only the field and the active
   * column cannot toggle away from the controlled sortOrder.
   */
  onSortOrderChange?: ((order: 'asc' | 'desc') => void) | undefined;
  /** Optional cursor pagination. */
  nextCursor?: string | null | undefined;
  prevCursor?: string | null | undefined;
  onCursorChange?: ((cursor: string) => void) | undefined;
  /** Spec 074 anchor scroll trigger — increment to scroll the marked row + pulse. */
  pulseTick?: number | undefined;
  /** Outer container variant. */
  panel?: 'default' | 'borderless';
  /**
   * Desktop `<tbody>` row shell — `inset` matches nested `Panel` + `DataTable`
   * padding math; `feed` matches launchpad proposal feed list cards (border,
   * `px-4 py-4` cells except identity column `pl-2 pr-4 py-4`, header rhythm).
   */
  desktopRowChrome?: 'inset' | 'feed' | undefined;
  /**
   * Viewport at which the desktop `<table>` replaces stacked cards.
   * Default `md`. Use `xl` when the table has too many columns for a tablet.
   */
  cardUntil?: 'md' | 'lg' | 'xl' | undefined;
  'data-testid'?: string;
  children: ReactNode;
}
function DataTableRoot(t0) {
  const $ = _c(34);
  const {
    entries,
    sortBy,
    sortOrder,
    onSortChange,
    onSortOrderChange,
    nextCursor,
    prevCursor,
    onCursorChange,
    pulseTick,
    panel: t1,
    desktopRowChrome: t2,
    cardUntil: t3,
    "data-testid": dataTestId,
    children
  } = t0;
  const panel = t1 === undefined ? "default" : t1;
  const desktopRowChrome = t2 === undefined ? "inset" : t2;
  const cardUntil = t3 === undefined ? "md" : t3;
  const prefersReducedMotion = useReducedMotion();
  const hydrated = useHydrated();
  let t4;
  if ($[0] !== cardUntil) {
    t4 = dataTableDesktopMedia(cardUntil);
    $[0] = cardUntil;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const isDesktop = useMediaQuery(t4);
  let t5;
  if ($[2] !== cardUntil) {
    t5 = dataTableLayoutClassNames(cardUntil);
    $[2] = cardUntil;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const layoutClassNames = t5;
  const tableRootRef = useRef(null);
  useCurrentUserRowPulse(tableRootRef, pulseTick, prefersReducedMotion === true);
  const t6 = sortBy as string;
  const t7 = onSortChange as (sort: string) => void;
  let t8;
  if ($[4] !== onSortOrderChange || $[5] !== prefersReducedMotion || $[6] !== sortOrder || $[7] !== t6 || $[8] !== t7) {
    t8 = {
      sortBy: t6,
      sortOrder,
      onSortChange: t7,
      onSortOrderChange,
      prefersReducedMotion
    };
    $[4] = onSortOrderChange;
    $[5] = prefersReducedMotion;
    $[6] = sortOrder;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const sortContextValue = t8;
  let headerNode;
  let t9;
  if ($[10] !== children || $[11] !== entries) {
    const {
      headerNode: t10,
      bodyNode
    } = findDataTableSections(children);
    headerNode = t10;
    t9 = isValidElement(bodyNode) && bodyNode !== null ? cloneBodyWithEntries(bodyNode, entries) : null;
    $[10] = children;
    $[11] = entries;
    $[12] = headerNode;
    $[13] = t9;
  } else {
    headerNode = $[12];
    t9 = $[13];
  }
  const desktopBody = t9;
  const t10 = !hydrated || isDesktop;
  const t11 = !hydrated || !isDesktop;
  let t12;
  if ($[14] !== dataTestId || $[15] !== desktopBody || $[16] !== desktopRowChrome || $[17] !== headerNode || $[18] !== layoutClassNames || $[19] !== nextCursor || $[20] !== onCursorChange || $[21] !== prevCursor || $[22] !== t10 || $[23] !== t11) {
    t12 = <DataTableLayouts rootRef={tableRootRef} mountDesktop={t10} mountMobile={t11} headerNode={headerNode} bodyNode={desktopBody} prevCursor={prevCursor} nextCursor={nextCursor} onCursorChange={onCursorChange} desktopRowChrome={desktopRowChrome} layoutClassNames={layoutClassNames} dataTestId={dataTestId} />;
    $[14] = dataTestId;
    $[15] = desktopBody;
    $[16] = desktopRowChrome;
    $[17] = headerNode;
    $[18] = layoutClassNames;
    $[19] = nextCursor;
    $[20] = onCursorChange;
    $[21] = prevCursor;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== sortContextValue || $[26] !== t12) {
    t13 = <SortContext.Provider value={sortContextValue}>{t12}</SortContext.Provider>;
    $[25] = sortContextValue;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== desktopRowChrome || $[29] !== t13) {
    t14 = <DesktopRowChromeContext.Provider value={desktopRowChrome}>{t13}</DesktopRowChromeContext.Provider>;
    $[28] = desktopRowChrome;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const inner = t14;
  let t15;
  if ($[31] !== inner || $[32] !== panel) {
    t15 = <LazyMotion features={domAnimation}>{panel === "default" ? <Panel surface="solid" inheritBackground={true} className="rounded-card px-3 pt-3 pb-1">{inner}</Panel> : inner}</LazyMotion>;
    $[31] = inner;
    $[32] = panel;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}
function useCurrentUserRowPulse(rootRef, pulseTick, reducedMotion) {
  const $ = _c(5);
  const previousPulseRef = useRef(undefined);
  let t0;
  let t1;
  if ($[0] !== pulseTick || $[1] !== reducedMotion || $[2] !== rootRef) {
    t0 = () => {
      if (pulseTick === undefined || previousPulseRef.current === pulseTick) {
        return;
      }
      previousPulseRef.current = pulseTick;
      const row = rootRef.current?.querySelector("[data-current-user-row=\"true\"]");
      if (row === null || row === undefined) {
        return;
      }
      row.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "center"
      });
      row.setAttribute("data-pulse", "true");
      const timer = window.setTimeout(() => row.removeAttribute("data-pulse"), reducedMotion ? 50 : 800);
      return () => window.clearTimeout(timer);
    };
    t1 = [pulseTick, reducedMotion, rootRef];
    $[0] = pulseTick;
    $[1] = reducedMotion;
    $[2] = rootRef;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}
function findDataTableSections(children: ReactNode): {
  headerNode: ReactNode;
  bodyNode: ReactNode;
} {
  let headerNode: ReactNode = null;
  let bodyNode: ReactNode = null;
  Children.forEach(children, child => {
    if (!isValidElement(child)) return;
    // ALLOW-UNKNOWN: generic primitive accepts children with arbitrary props; only displayName is read.
    const childType = child.type as ComponentType<unknown> & {
      displayName?: string;
    };
    if (childType === DataTableHeader || childType.displayName === 'DataTableHeader') {
      headerNode = child;
    } else if (childType === DataTableBody || childType.displayName === 'DataTableBody') {
      bodyNode = child;
    }
  });
  return {
    headerNode,
    bodyNode
  };
}
interface DataTableLayoutsProps {
  rootRef: RefObject<HTMLDivElement | null>;
  mountDesktop: boolean;
  mountMobile: boolean;
  headerNode: ReactNode;
  bodyNode: ReactNode;
  prevCursor: string | null | undefined;
  nextCursor: string | null | undefined;
  onCursorChange: ((cursor: string) => void) | undefined;
  desktopRowChrome: 'inset' | 'feed';
  layoutClassNames: {
    desktop: string;
    mobile: string;
  };
  dataTestId: string | undefined;
}
function DataTableLayouts(t0) {
  const $ = _c(24);
  const {
    rootRef,
    mountDesktop,
    mountMobile,
    headerNode,
    bodyNode,
    prevCursor,
    nextCursor,
    onCursorChange,
    desktopRowChrome,
    layoutClassNames,
    dataTestId
  } = t0;
  const t1 = desktopRowChrome === "feed" ? "gap-0" : "gap-2";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex flex-col", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== bodyNode || $[3] !== desktopRowChrome || $[4] !== headerNode || $[5] !== layoutClassNames || $[6] !== mountDesktop) {
    t3 = mountDesktop ? <LayoutContext.Provider value="desktop"><div className={layoutClassNames.desktop} data-data-table-layout="desktop"><Table className={cn("w-full table-fixed border-separate border-spacing-y-2", desktopRowChrome === "feed" && "-mt-2")}>{headerNode}{bodyNode}</Table></div></LayoutContext.Provider> : null;
    $[2] = bodyNode;
    $[3] = desktopRowChrome;
    $[4] = headerNode;
    $[5] = layoutClassNames;
    $[6] = mountDesktop;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== bodyNode || $[9] !== layoutClassNames || $[10] !== mountMobile) {
    t4 = mountMobile ? <LayoutContext.Provider value="mobile"><div className={layoutClassNames.mobile} data-data-table-layout="mobile">{bodyNode}</div></LayoutContext.Provider> : null;
    $[8] = bodyNode;
    $[9] = layoutClassNames;
    $[10] = mountMobile;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== desktopRowChrome || $[13] !== nextCursor || $[14] !== onCursorChange || $[15] !== prevCursor) {
    t5 = <DataTablePagination prevCursor={prevCursor} nextCursor={nextCursor} onCursorChange={onCursorChange} desktopRowChrome={desktopRowChrome} />;
    $[12] = desktopRowChrome;
    $[13] = nextCursor;
    $[14] = onCursorChange;
    $[15] = prevCursor;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== dataTestId || $[18] !== rootRef || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div ref={rootRef} className={t2} data-testid={dataTestId}>{t3}{t4}{t5}</div>;
    $[17] = dataTestId;
    $[18] = rootRef;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
function DataTablePagination(t0) {
  const $ = _c(26);
  const {
    prevCursor,
    nextCursor,
    onCursorChange,
    desktopRowChrome
  } = t0;
  const tNav = useTranslations("ui.data_table");
  const showPagination = onCursorChange !== undefined && (isAvailableCursor(prevCursor) || isAvailableCursor(nextCursor));
  if (!showPagination) {
    return null;
  }
  const t1 = desktopRowChrome === "feed" ? "px-4" : "px-2";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex items-center justify-end gap-2 pt-2", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== tNav) {
    t3 = tNav("pagination_prev_aria");
    $[2] = tNav;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = prevCursor === null || prevCursor === undefined;
  let t5;
  if ($[4] !== onCursorChange || $[5] !== prevCursor) {
    t5 = () => selectCursor(prevCursor, onCursorChange);
    $[4] = onCursorChange;
    $[5] = prevCursor;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={ArrowLeft01Icon} className="size-4" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t7 = <AnimatedButton variant="tertiary" size="sm" type="button" aria-label={t3} disabled={t4} onClick={t5}>{t6}</AnimatedButton>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== tNav) {
    t8 = tNav("pagination_next_aria");
    $[12] = tNav;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = nextCursor === null || nextCursor === undefined;
  let t10;
  if ($[14] !== nextCursor || $[15] !== onCursorChange) {
    t10 = () => selectCursor(nextCursor, onCursorChange);
    $[14] = nextCursor;
    $[15] = onCursorChange;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Icon icon={ArrowRight01Icon} className="size-4" />;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t12 = <AnimatedButton variant="tertiary" size="sm" type="button" aria-label={t8} disabled={t9} onClick={t10}>{t11}</AnimatedButton>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t2 || $[24] !== t7) {
    t13 = <div className={t2}>{t7}{t12}</div>;
    $[22] = t12;
    $[23] = t2;
    $[24] = t7;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
}
function isAvailableCursor(cursor: string | null | undefined): cursor is string {
  return cursor !== null && cursor !== undefined;
}
function selectCursor(cursor: string | null | undefined, onCursorChange: ((cursor: string) => void) | undefined): void {
  if (cursor === null || cursor === undefined || onCursorChange === undefined) return;
  onCursorChange(cursor);
}

// Helper: re-inject entries into the Body node so its render-prop can run.
// Returns the Body unchanged if no entries cloning is needed (Body reads via
// EntriesContext below).
// ALLOW-UNKNOWN: generic primitive — entries shape is consumer-defined; Body reads via EntriesContext.
function cloneBodyWithEntries(bodyNode: ReactNode, _entries: ReadonlyArray<unknown>): ReactNode {
  return bodyNode;
}

// ---------------------------------------------------------------------------
// DataTable.Header — column declarations live here
// ---------------------------------------------------------------------------

interface DataTableHeaderProps {
  children: ReactNode;
}
function DataTableHeader(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const layout = useLayout();
  const chrome = useDesktopRowChrome();
  const isFeed = chrome === "feed";
  if (layout === "mobile") {
    return null;
  }
  const t1 = isFeed ? "text-foreground-tertiary [&>th]:px-4 [&>th]:pt-4 [&>th]:pb-2 [&>th:first-child]:px-1" : "text-foreground-secondary [&>th:first-child]:pl-4 [&>th:last-child]:pr-4";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("border-b border-border-primary bg-transparent hover:bg-transparent text-caption [&>th]:bg-transparent [&>th:hover]:bg-transparent", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <TableHeader><TableRow className={t2}>{children}</TableRow></TableHeader>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
DataTableHeader.displayName = 'DataTableHeader';

// ---------------------------------------------------------------------------
// DataTable.RankColumn — sr-only header for the rank column
// ---------------------------------------------------------------------------

interface DataTableRankColumnProps {
  className?: string;
}
function DataTableRankColumn(t0) {
  const $ = _c(11);
  const {
    className
  } = t0;
  const t = useTranslations("ui.data_table");
  const isFeed = useDesktopRowChrome() === "feed";
  const t1 = isFeed ? COL_RANK_CLASS_FEED : COL_RANK_CLASS_INSET;
  const t2 = isFeed ? "h-auto select-none text-center font-medium" : "select-none px-2 pb-2 text-left font-medium";
  let t3;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2) {
    t3 = cn(t1, t2, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("rank_column_aria");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <span className="sr-only">{t4}</span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <TableHead scope="col" className={t3}>{t5}</TableHead>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
DataTableRankColumn.displayName = 'DataTableRankColumn';

// ---------------------------------------------------------------------------
// DataTable.IdentityColumn — labelled column for the identity column
// ---------------------------------------------------------------------------

interface DataTableIdentityColumnProps {
  header: string;
  className?: string;
}
function DataTableIdentityColumn(t0) {
  const $ = _c(6);
  const {
    header,
    className
  } = t0;
  const isFeed = useDesktopRowChrome() === "feed";
  let t1;
  if ($[0] !== className || $[1] !== isFeed) {
    t1 = cn(identityColClass(isFeed), isFeed ? "h-auto select-none pl-2 pr-4 text-left font-medium text-foreground-tertiary" : "select-none px-2 pb-2 text-left font-medium text-foreground-secondary", className);
    $[0] = className;
    $[1] = isFeed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== header || $[4] !== t1) {
    t2 = <TableHead scope="col" className={t1}>{header}</TableHead>;
    $[3] = header;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
DataTableIdentityColumn.displayName = 'DataTableIdentityColumn';

// ---------------------------------------------------------------------------
// DataTable.SortableColumn — clickable sortable header
// ---------------------------------------------------------------------------

interface DataTableSortableColumnProps<TSort extends string> {
  sortKey: TSort;
  header: string;
  headerTooltip?: string;
  align?: 'left' | 'right';
  hideOnMobile?: boolean;
  className?: string;
}
function DataTableSortableColumn(t0) {
  const $ = _c(47);
  const {
    sortKey,
    header,
    headerTooltip,
    align: t1,
    className
  } = t0;
  const align = t1 === undefined ? "right" : t1;
  const ctx = useSortContext();
  const reduceMotion = ctx.prefersReducedMotion === true;
  const isFeed = useDesktopRowChrome() === "feed";
  const isActive = ctx.sortBy === sortKey;
  const ariaSortValue = !isActive ? "none" : ctx.sortOrder === "asc" ? "ascending" : "descending";
  let t2;
  if ($[0] !== reduceMotion) {
    t2 = reduceMotion ? {
      duration: 0
    } as const : LEADERBOARD_SORT_MICRO;
    $[0] = reduceMotion;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const rotateTransition = t2;
  let t3;
  if ($[2] !== reduceMotion) {
    t3 = reduceMotion ? {
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0
        }
      },
      hidden: {
        opacity: 0,
        y: 0,
        transition: {
          duration: 0
        }
      }
    } as const : SORT_INDICATOR_VARIANTS;
    $[2] = reduceMotion;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const variants = t3;
  let t4;
  if ($[4] !== ctx || $[5] !== sortKey) {
    t4 = function handleSort() {
      if (ctx.sortBy === sortKey) {
        ctx.onSortOrderChange?.(ctx.sortOrder === "asc" ? "desc" : "asc");
      } else {
        ctx.onSortChange(sortKey);
        ctx.onSortOrderChange?.("desc");
      }
    };
    $[4] = ctx;
    $[5] = sortKey;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleSort = t4;
  let t5;
  if ($[7] !== handleSort) {
    t5 = function handleKeyDown(e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleSort();
      }
    };
    $[7] = handleSort;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleKeyDown = t5;
  const t6 = isFeed ? "h-auto text-foreground-tertiary hover:text-foreground-primary" : "text-foreground-secondary hover:text-foreground-primary";
  const t7 = !isFeed && "pb-2";
  const t8 = align === "right" ? "text-right" : "text-left";
  const t9 = isFeed ? "px-0" : "px-2";
  let t10;
  if ($[9] !== className || $[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = cn("group/sort-header select-none cursor-pointer font-medium bg-transparent hover:bg-transparent outline-none transition-colors focus-visible:ring-2 focus-visible:ring-border-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background-secondary", t6, t7, t8, t9, className);
    $[9] = className;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = align === "right" ? "justify-end" : "justify-start";
  let t12;
  if ($[15] !== t11) {
    t12 = cn("inline-flex w-full min-w-0 items-center gap-0.5", t11);
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== align || $[18] !== ctx.onSortOrderChange || $[19] !== ctx.sortOrder || $[20] !== isActive || $[21] !== rotateTransition || $[22] !== sortKey || $[23] !== variants) {
    t13 = align === "right" && <SortChevronSlot isActive={isActive} sortKey={sortKey} sortOrder={ctx.sortOrder} previewSortOrder={ctx.onSortOrderChange === undefined ? ctx.sortOrder : "asc"} rotateTransition={rotateTransition} variants={variants as unknown as typeof SORT_INDICATOR_VARIANTS} />;
    $[17] = align;
    $[18] = ctx.onSortOrderChange;
    $[19] = ctx.sortOrder;
    $[20] = isActive;
    $[21] = rotateTransition;
    $[22] = sortKey;
    $[23] = variants;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== header) {
    t14 = <span className="inline-block whitespace-nowrap">{header}</span>;
    $[25] = header;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== align || $[28] !== ctx.onSortOrderChange || $[29] !== ctx.sortOrder || $[30] !== isActive || $[31] !== rotateTransition || $[32] !== sortKey || $[33] !== variants) {
    t15 = align === "left" && <SortChevronSlot isActive={isActive} sortKey={sortKey} sortOrder={ctx.sortOrder} previewSortOrder={ctx.onSortOrderChange === undefined ? ctx.sortOrder : "asc"} rotateTransition={rotateTransition} variants={variants as unknown as typeof SORT_INDICATOR_VARIANTS} />;
    $[27] = align;
    $[28] = ctx.onSortOrderChange;
    $[29] = ctx.sortOrder;
    $[30] = isActive;
    $[31] = rotateTransition;
    $[32] = sortKey;
    $[33] = variants;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t12 || $[36] !== t13 || $[37] !== t14 || $[38] !== t15) {
    t16 = <span className={t12}>{t13}{t14}{t15}</span>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== ariaSortValue || $[41] !== handleKeyDown || $[42] !== handleSort || $[43] !== headerTooltip || $[44] !== t10 || $[45] !== t16) {
    t17 = <TableHead scope="col" aria-sort={ariaSortValue} tabIndex={0} title={headerTooltip} className={t10} onClick={handleSort} onKeyDown={handleKeyDown}>{t16}</TableHead>;
    $[40] = ariaSortValue;
    $[41] = handleKeyDown;
    $[42] = handleSort;
    $[43] = headerTooltip;
    $[44] = t10;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
}

// ALLOW-UNKNOWN: motion variants are heavily typed; the reduced-motion
// branch returns a structurally different transition shape. We cast through
// `unknown` at the call site rather than carry that variance through the prop.
interface SortChevronSlotProps {
  isActive: boolean;
  sortKey: string;
  sortOrder: 'asc' | 'desc';
  previewSortOrder: 'asc' | 'desc';
  rotateTransition: {
    duration: number;
  } | typeof LEADERBOARD_SORT_MICRO;
  variants: typeof SORT_INDICATOR_VARIANTS;
}
function SortChevronSlot(t0) {
  const $ = _c(18);
  const {
    isActive,
    sortKey,
    sortOrder,
    previewSortOrder,
    rotateTransition,
    variants
  } = t0;
  const t1 = isActive === false && "pointer-events-none";
  let t2;
  if ($[0] !== t1) {
    t2 = cn(SORT_HEADER_ICON_BOX, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isActive === false;
  let t4;
  if ($[2] !== isActive || $[3] !== rotateTransition || $[4] !== sortKey || $[5] !== sortOrder || $[6] !== variants) {
    t4 = isActive ? <m.span key={sortKey} variants={variants} initial="hidden" animate="visible" exit="hidden" className={SORT_HEADER_ICON_BOX}><m.span initial={false} animate={{
        rotate: sortOrder === "asc" ? 0 : 180
      }} transition={rotateTransition} className={cn(SORT_HEADER_ICON_BOX, "align-middle")}><Icon icon={ArrowUp01IconSolid} className="size-3" /></m.span></m.span> : null;
    $[2] = isActive;
    $[3] = rotateTransition;
    $[4] = sortKey;
    $[5] = sortOrder;
    $[6] = variants;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <AnimatePresence initial={false}>{t4}</AnimatePresence>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isActive || $[11] !== previewSortOrder) {
    t6 = isActive ? null : <span className={cn(SORT_HEADER_ICON_BOX, "opacity-0 transition-opacity duration-100 group-hover/sort-header:opacity-100 group-focus-visible/sort-header:opacity-100", previewSortOrder === "desc" && "rotate-180")}><Icon icon={ArrowUp01IconSolid} className="size-3" /></span>;
    $[10] = isActive;
    $[11] = previewSortOrder;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t5 || $[16] !== t6) {
    t7 = <span className={t2} aria-hidden={t3}>{t5}{t6}</span>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
DataTableSortableColumn.displayName = 'DataTableSortableColumn';

// ---------------------------------------------------------------------------
// DataTable.Column — non-sortable header
// ---------------------------------------------------------------------------

interface DataTableColumnProps {
  header: string;
  headerTooltip?: string;
  align?: 'left' | 'right';
  hideOnMobile?: boolean;
  className?: string;
}
function DataTableColumn(t0) {
  const $ = _c(8);
  const {
    header,
    headerTooltip,
    align: t1,
    className
  } = t0;
  const align = t1 === undefined ? "right" : t1;
  const isFeed = useDesktopRowChrome() === "feed";
  const t2 = isFeed ? "h-auto px-0 text-foreground-tertiary" : "pb-2 px-2 text-foreground-secondary";
  const t3 = align === "right" ? "text-right" : "text-left";
  let t4;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3) {
    t4 = cn("select-none font-medium", t2, t3, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== header || $[5] !== headerTooltip || $[6] !== t4) {
    t5 = <TableHead scope="col" title={headerTooltip} className={t4}>{header}</TableHead>;
    $[4] = header;
    $[5] = headerTooltip;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
DataTableColumn.displayName = 'DataTableColumn';

// ---------------------------------------------------------------------------
// DataTable.Body — render-prop child invoked per entry
// ---------------------------------------------------------------------------

interface DataTableBodyProps<TEntry> {
  /** Render function — receives the row entry and index; must return a <DataTable.Row>. */
  children: (entry: TEntry, index: number) => ReactNode;
}
function DataTableBody(t0) {
  const $ = _c(14);
  const {
    children: render
  } = t0;
  const layout = useLayout();
  const entries = useEntriesContext();
  if (layout === "desktop") {
    let t1;
    if ($[0] !== entries || $[1] !== render) {
      let t2;
      if ($[3] !== render) {
        t2 = (entry, i) => render(entry, i);
        $[3] = render;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t1 = entries.map(t2);
      $[0] = entries;
      $[1] = render;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[5] !== t1) {
      t2 = <TableBody>{t1}</TableBody>;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t1;
  if ($[7] !== entries || $[8] !== render) {
    let t2;
    if ($[10] !== render) {
      t2 = (entry_0, i_0) => render(entry_0, i_0);
      $[10] = render;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    t1 = entries.map(t2);
    $[7] = entries;
    $[8] = render;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[12] !== t1) {
    t2 = <>{t1}</>;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}
DataTableBody.displayName = 'DataTableBody';

// EntriesContext — set by the root, read by Body.
// ALLOW-UNKNOWN: Context cannot carry the generic TEntry across the React boundary; consumers re-narrow via useEntriesContext<TEntry>.
const EntriesContext: Context<ReadonlyArray<unknown> | null> =
// ALLOW-UNKNOWN: Context cannot carry the generic TEntry across the React boundary; consumers re-narrow via useEntriesContext<TEntry>.
createContext<ReadonlyArray<unknown> | null>(null);
function useEntriesContext<TEntry>(): ReadonlyArray<TEntry> {
  const ctx = use(EntriesContext);
  if (ctx === null) {
    throw new Error('DataTable.Body must be used inside <DataTable>');
  }
  return ctx as ReadonlyArray<TEntry>;
}

// ---------------------------------------------------------------------------
// DataTable.Row — one row card
// ---------------------------------------------------------------------------

interface DataTableRowProps {
  /** Marks the row as the current user's row (drives `data-current-user-row`). */
  isCurrentUser?: boolean;
  /**
   * Destination for whole-row navigation. When set, the row renders a real
   * `<Link>` (desktop: a stretched overlay anchor in the rank cell; mobile: the
   * full-row `<Link>` replaces the tap button) instead of an imperative
   * `onClick` push — so the destination RSC + dehydrated SDK cache viewport-
   * prefetch and clicks are soft navs. Prefer this over `onClick` for user-
   * intent navigation; `onClick` remains for rows that act, not navigate.
   *
   * Two constraints on the desktop overlay (a `<tr>` may not be an anchor, so
   * the anchor is painted from the rank cell at `absolute inset-0 z-10`):
   *   1. The row MUST lead with a `<DataTable.RankCell>` — that's where the
   *      overlay anchor is rendered. An `href` row without one has no clickable
   *      surface on desktop (it would look interactive but do nothing).
   *   2. No other cell in the row may contain its own interactive control
   *      (link/button) — the overlay sits above the sibling cells and would
   *      mask it. Such a control must be lifted into its own higher stacking
   *      layer, or that surface must stay on the `onClick` model, not `href`.
   */
  href?: string;
  /** Click handler. Mobile: button; desktop: row click. Ignored when `href` is set. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /**
   * Hover/focus/touch intent — typically the spread of
   * `useIntentPrefetch(...)` from `@morfi/backend-sdk-v2/client`. Forwarded
   * to both the desktop row and the mobile button so caches warm on intent.
   *
   * Prefer passing `intent={...}` (the full `IntentHandlers` triple from the
   * SDK) over the discrete `onMouseEnter` / `onFocus` / `onPointerDown` props
   * — the typed bag tracks the SDK's `IntentHandlers` contract, so any
   * future intent surface lands on every consumer at once. The discrete
   * props are still accepted (and win when both are supplied) for callers
   * that need to pass a different handler per event.
   */
  intent?: IntentHandlers;
  onMouseEnter?: () => void;
  onFocus?: () => void;
  onPointerDown?: () => void;
  /** ARIA label for keyboard users. */
  ariaLabel?: string;
  'data-testid'?: string;
  className?: string;
  children: ReactNode;
}
interface RenderedDataTableRowProps {
  isCurrentUser: boolean | undefined;
  href: string | undefined;
  onClick: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  onMouseEnter: (() => void) | undefined;
  onFocus: (() => void) | undefined;
  onPointerDown: (() => void) | undefined;
  ariaLabel: string | undefined;
  dataTestId: string | undefined;
  className: string | undefined;
  children: ReactNode;
  isFeed: boolean;
  reduceMotion: boolean;
}
function DataTableRow(t0) {
  const $ = _c(26);
  const {
    isCurrentUser,
    href,
    onClick,
    intent,
    onMouseEnter: onMouseEnterProp,
    onFocus: onFocusProp,
    onPointerDown: onPointerDownProp,
    ariaLabel,
    "data-testid": dataTestId,
    className,
    children
  } = t0;
  const onMouseEnter = onMouseEnterProp ?? intent?.onMouseEnter;
  const onFocus = onFocusProp ?? intent?.onFocus;
  const onPointerDown = onPointerDownProp ?? intent?.onPointerDown;
  const layout = useLayout();
  const chrome = useDesktopRowChrome();
  const isFeed = chrome === "feed";
  const reduceMotion = useReducedMotion() === true;
  if (layout === "desktop") {
    let t1;
    if ($[0] !== ariaLabel || $[1] !== children || $[2] !== className || $[3] !== dataTestId || $[4] !== href || $[5] !== isCurrentUser || $[6] !== isFeed || $[7] !== onClick || $[8] !== onFocus || $[9] !== onMouseEnter || $[10] !== onPointerDown || $[11] !== reduceMotion) {
      t1 = <DataTableDesktopRow isCurrentUser={isCurrentUser} href={href} onClick={onClick} onMouseEnter={onMouseEnter} onFocus={onFocus} onPointerDown={onPointerDown} ariaLabel={ariaLabel} dataTestId={dataTestId} className={className} isFeed={isFeed} reduceMotion={reduceMotion}>{children}</DataTableDesktopRow>;
      $[0] = ariaLabel;
      $[1] = children;
      $[2] = className;
      $[3] = dataTestId;
      $[4] = href;
      $[5] = isCurrentUser;
      $[6] = isFeed;
      $[7] = onClick;
      $[8] = onFocus;
      $[9] = onMouseEnter;
      $[10] = onPointerDown;
      $[11] = reduceMotion;
      $[12] = t1;
    } else {
      t1 = $[12];
    }
    return t1;
  }
  let t1;
  if ($[13] !== ariaLabel || $[14] !== children || $[15] !== className || $[16] !== dataTestId || $[17] !== href || $[18] !== isCurrentUser || $[19] !== isFeed || $[20] !== onClick || $[21] !== onFocus || $[22] !== onMouseEnter || $[23] !== onPointerDown || $[24] !== reduceMotion) {
    t1 = <DataTableMobileRow isCurrentUser={isCurrentUser} href={href} onClick={onClick} onMouseEnter={onMouseEnter} onFocus={onFocus} onPointerDown={onPointerDown} ariaLabel={ariaLabel} dataTestId={dataTestId} className={className} isFeed={isFeed} reduceMotion={reduceMotion}>{children}</DataTableMobileRow>;
    $[13] = ariaLabel;
    $[14] = children;
    $[15] = className;
    $[16] = dataTestId;
    $[17] = href;
    $[18] = isCurrentUser;
    $[19] = isFeed;
    $[20] = onClick;
    $[21] = onFocus;
    $[22] = onMouseEnter;
    $[23] = onPointerDown;
    $[24] = reduceMotion;
    $[25] = t1;
  } else {
    t1 = $[25];
  }
  return t1;
}
DataTableRow.displayName = 'DataTableRow';
function DataTableDesktopRow(t0) {
  const $ = _c(34);
  const {
    isCurrentUser,
    href,
    onClick,
    onMouseEnter,
    onFocus,
    onPointerDown,
    ariaLabel,
    dataTestId,
    className,
    children,
    isFeed,
    reduceMotion
  } = t0;
  const isLink = href !== undefined;
  let t1;
  if ($[0] !== ariaLabel || $[1] !== href || $[2] !== isLink || $[3] !== onFocus || $[4] !== onMouseEnter || $[5] !== onPointerDown) {
    t1 = isLink ? {
      href,
      ariaLabel,
      onMouseEnter,
      onFocus,
      onPointerDown
    } : null;
    $[0] = ariaLabel;
    $[1] = href;
    $[2] = isLink;
    $[3] = onFocus;
    $[4] = onMouseEnter;
    $[5] = onPointerDown;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const rowLink = t1;
  let t2;
  if ($[7] !== onClick) {
    t2 = function handleKey(event) {
      if (onClick === undefined) {
        return;
      }
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
      onClick(event as unknown as MouseEvent<HTMLElement>);
    };
    $[7] = onClick;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleKey = t2;
  const t3 = !isLink && onClick !== undefined ? 0 : undefined;
  const t4 = isLink ? undefined : ariaLabel;
  let t5;
  if ($[9] !== isCurrentUser) {
    t5 = isCurrentUser === true ? {
      "data-current-user-row": "true"
    } : {};
    $[9] = isCurrentUser;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = isLink || onClick !== undefined;
  const t7 = onClick !== undefined;
  const t8 = isCurrentUser === true;
  let t9;
  if ($[11] !== className || $[12] !== isFeed || $[13] !== isLink || $[14] !== reduceMotion || $[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
    t9 = desktopRowClassName({
      isLink,
      isInteractive: t6,
      hasClickHandler: t7,
      isCurrentUser: t8,
      isFeed,
      reduceMotion,
      className
    });
    $[11] = className;
    $[12] = isFeed;
    $[13] = isLink;
    $[14] = reduceMotion;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = isLink ? undefined : onClick;
  const t11 = isLink ? undefined : handleKey;
  const t12 = isLink ? undefined : onMouseEnter;
  const t13 = isLink ? undefined : onFocus;
  const t14 = isLink ? undefined : onPointerDown;
  let t15;
  if ($[19] !== children || $[20] !== dataTestId || $[21] !== t10 || $[22] !== t11 || $[23] !== t12 || $[24] !== t13 || $[25] !== t14 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t9) {
    t15 = <TableRow tabIndex={t3} aria-label={t4} data-testid={dataTestId} {...t5} className={t9} onClick={t10} onKeyDown={t11} onMouseEnter={t12} onFocus={t13} onPointerDown={t14}>{children}</TableRow>;
    $[19] = children;
    $[20] = dataTestId;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== rowLink || $[32] !== t15) {
    t16 = <RowLinkContext.Provider value={rowLink}>{t15}</RowLinkContext.Provider>;
    $[31] = rowLink;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
}
function desktopRowClassName({
  isLink,
  isInteractive,
  hasClickHandler,
  isCurrentUser,
  isFeed,
  reduceMotion,
  className
}: {
  isLink: boolean;
  isInteractive: boolean;
  hasClickHandler: boolean;
  isCurrentUser: boolean;
  isFeed: boolean;
  reduceMotion: boolean;
  className: string | undefined;
}): string {
  const chromeClassName = isFeed ? cn(
  // Match the td-corner radius on the <tr> itself so the hover box-shadow
  // follows the rounded corners instead of painting a square outline.
  'min-h-14 rounded-[var(--radius-card)] bg-background-primary [&>td]:transition-colors', FEED_DESKTOP_ROW_OUTLINE_CLASS, 'hover:bg-background-primary', FEED_CARD_HOVER_BACKGROUND, 'hover:[&>td]:border-border-primary data-[pulse=true]:bg-foreground-brand/15 data-[pulse=true]:transition-colors', reduceMotion === false && 'transition-[box-shadow,border-color,background-color] duration-fast ease-default motion-reduce:transition-none hover:shadow-elevation-card') : cn(
  // The inset radius and padding match the surrounding Panel geometry.
  'min-h-14 bg-background-primary transition-colors [&>td:first-child]:rounded-l-panel-inset [&>td:first-child]:pl-4 [&>td:last-child]:rounded-r-panel-inset [&>td:last-child]:pr-4', FEED_CARD_HOVER_BACKGROUND, 'data-[pulse=true]:bg-foreground-brand/15 data-[pulse=true]:transition-colors');
  return cn(
  // Safari does not use a positioned <tr> as the overlay's containing block.
  isLink && 'relative transform-[translate(0)]', chromeClassName, isInteractive && 'cursor-pointer', !isLink && hasClickHandler && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background-secondary', isCurrentUser && 'bg-foreground-primary/5', className);
}
function DataTableMobileRow(t0) {
  const $ = _c(30);
  const {
    isCurrentUser,
    href,
    onClick,
    onMouseEnter,
    onFocus,
    onPointerDown,
    ariaLabel,
    dataTestId,
    className,
    children,
    isFeed,
    reduceMotion
  } = t0;
  const isLink = href !== undefined;
  const t1 = isLink || onClick !== undefined;
  const t2 = isCurrentUser === true;
  let t3;
  if ($[0] !== className || $[1] !== isFeed || $[2] !== reduceMotion || $[3] !== t1 || $[4] !== t2) {
    t3 = mobileRowClassName({
      isFeed,
      reduceMotion,
      isInteractive: t1,
      isCurrentUser: t2,
      className
    });
    $[0] = className;
    $[1] = isFeed;
    $[2] = reduceMotion;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const rowClassName = t3;
  if (isLink) {
    let t4;
    if ($[6] !== isCurrentUser) {
      t4 = isCurrentUser === true ? {
        "data-current-user-row": "true"
      } : {};
      $[6] = isCurrentUser;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== ariaLabel || $[9] !== children || $[10] !== dataTestId || $[11] !== href || $[12] !== onFocus || $[13] !== onMouseEnter || $[14] !== onPointerDown || $[15] !== rowClassName || $[16] !== t4) {
      t5 = <Link href={href} aria-label={ariaLabel} data-testid={dataTestId} {...t4} onMouseEnter={onMouseEnter} onFocus={onFocus} onPointerDown={onPointerDown} className={rowClassName}>{children}</Link>;
      $[8] = ariaLabel;
      $[9] = children;
      $[10] = dataTestId;
      $[11] = href;
      $[12] = onFocus;
      $[13] = onMouseEnter;
      $[14] = onPointerDown;
      $[15] = rowClassName;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  let t4;
  if ($[18] !== isCurrentUser) {
    t4 = isCurrentUser === true ? {
      "data-current-user-row": "true"
    } : {};
    $[18] = isCurrentUser;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== ariaLabel || $[21] !== children || $[22] !== dataTestId || $[23] !== onClick || $[24] !== onFocus || $[25] !== onMouseEnter || $[26] !== onPointerDown || $[27] !== rowClassName || $[28] !== t4) {
    t5 = <button type="button" aria-label={ariaLabel} data-testid={dataTestId} {...t4} onClick={onClick} onMouseEnter={onMouseEnter} onFocus={onFocus} onPointerDown={onPointerDown} className={rowClassName}>{children}</button>;
    $[20] = ariaLabel;
    $[21] = children;
    $[22] = dataTestId;
    $[23] = onClick;
    $[24] = onFocus;
    $[25] = onMouseEnter;
    $[26] = onPointerDown;
    $[27] = rowClassName;
    $[28] = t4;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  return t5;
}
function mobileRowClassName({
  isFeed,
  reduceMotion,
  isInteractive,
  isCurrentUser,
  className
}: {
  isFeed: boolean;
  reduceMotion: boolean;
  isInteractive: boolean;
  isCurrentUser: boolean;
  className: string | undefined;
}): string {
  return cn('flex w-full items-center gap-3 rounded-card bg-background-primary text-left transition-colors data-[pulse=true]:bg-foreground-brand/15', FEED_CARD_HOVER_BACKGROUND,
  // Inset rows sit inside an already-padded section, so a tight inner pad
  // avoids a doubled gutter; feed rows retain their wider card padding.
  isFeed ? 'px-4 py-4' : 'px-2 py-3', isFeed && FEED_CARD_RESTING_OUTLINE, isFeed && reduceMotion === false && FEED_CARD_OUTLINE_INTERACTION, isInteractive && 'cursor-pointer', isCurrentUser && 'bg-foreground-primary/5', className);
}

// ---------------------------------------------------------------------------
// DataTable.RankCell
// ---------------------------------------------------------------------------

export interface RankCellProps {
  /** 1-based rank, or null when the row is intentionally unranked. */
  value: number | null;
  /** Localized label shown when `value` is null. */
  unrankedLabel?: string;
  isCurrentUser?: boolean;
  /** Optional Δ rank shown next to the numeral. */
  deltaRank?: number;
  size?: 'compact' | 'display';
}
function DataTableRankCell(t0) {
  const $ = _c(24);
  const {
    value,
    unrankedLabel,
    isCurrentUser,
    deltaRank,
    size: t1
  } = t0;
  const size = t1 === undefined ? "display" : t1;
  const layout = useLayout();
  const cellPad = useDesktopCellPad();
  const chrome = useDesktopRowChrome();
  const rowLink = use(RowLinkContext);
  const tRank = useTranslations("ui.data_table");
  const rankCellPad = chrome === "feed" ? "px-1 py-4" : cellPad;
  const rankColClass = chrome === "feed" ? COL_RANK_CLASS_FEED : COL_RANK_CLASS_INSET;
  let t2;
  if ($[0] !== size || $[1] !== tRank || $[2] !== unrankedLabel || $[3] !== value) {
    t2 = value === null ? <span className="text-caption font-medium text-foreground-tertiary">{unrankedLabel ?? 0}</span> : value >= 1 && value <= 3 ? <RankBadge rank={value} size={size} table={true} trophyAriaLabel={tRank("rank_trophy_aria", {
      rank: value
    })} /> : <span className={cn(rankCellNumeralClassName(), "text-foreground-tertiary")}>{value}</span>;
    $[0] = size;
    $[1] = tRank;
    $[2] = unrankedLabel;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const numeral = t2;
  let t3;
  if ($[5] !== deltaRank || $[6] !== isCurrentUser || $[7] !== value) {
    t3 = value !== null && isCurrentUser === true && deltaRank !== undefined && deltaRank !== 0 ? <span className={cn("inline-flex items-center gap-0.5 font-mono tabular-nums text-caption font-medium", deltaRank > 0 ? "text-green-500" : "text-red-500")}><Icon icon={CircleArrowDown02IconSolid} className={cn("size-3", deltaRank > 0 && "rotate-180")} />{Math.abs(deltaRank)}</span> : null;
    $[5] = deltaRank;
    $[6] = isCurrentUser;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const delta = t3;
  if (layout === "desktop") {
    let t4;
    if ($[9] !== rankCellPad || $[10] !== rankColClass) {
      t4 = cn(rankCellPad, rankColClass);
      $[9] = rankCellPad;
      $[10] = rankColClass;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== rowLink) {
      t5 = rowLink !== null && <Link href={rowLink.href} aria-label={rowLink.ariaLabel} onMouseEnter={rowLink.onMouseEnter} onFocus={rowLink.onFocus} onPointerDown={rowLink.onPointerDown} className="absolute inset-0 z-10 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary focus-visible:ring-inset" />;
      $[12] = rowLink;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== delta || $[15] !== numeral) {
      t6 = <div className="flex min-w-0 items-center justify-center gap-1">{numeral}{delta}</div>;
      $[14] = delta;
      $[15] = numeral;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
      t7 = <TableCell className={t4}>{t5}{t6}</TableCell>;
      $[17] = t4;
      $[18] = t5;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  let t4;
  if ($[21] !== delta || $[22] !== numeral) {
    t4 = <div className="flex w-7 min-w-7 shrink-0 items-center justify-center">{numeral}{delta}</div>;
    $[21] = delta;
    $[22] = numeral;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  return t4;
}
DataTableRankCell.displayName = 'DataTableRankCell';

// ---------------------------------------------------------------------------
// DataTable.IdentityCell
// ---------------------------------------------------------------------------

export interface IdentityCellProps {
  user: UserIdentityShape;
  showYouBadge?: boolean;
  variant?: 'inline' | 'with-avatar';
  size?: 'sm' | 'md' | 'lg';
  avatarSize?: UserAvatarSize;
  className?: string;
  /** Translated "You" badge label (default: "You"). */
  youLabel?: string;
  /** Extra badge after the identity and the "You" badge (e.g. predicted winnings). */
  badge?: React.ReactNode | undefined;
}
function DataTableIdentityCell(t0) {
  const $ = _c(34);
  const {
    user,
    showYouBadge,
    variant: t1,
    size: t2,
    avatarSize,
    className,
    youLabel: t3,
    badge
  } = t0;
  const variant = t1 === undefined ? "with-avatar" : t1;
  const size = t2 === undefined ? "sm" : t2;
  const youLabel = t3 === undefined ? "You" : t3;
  const layout = useLayout();
  const cellPad = useDesktopCellPad();
  const isFeed = useDesktopRowChrome() === "feed";
  const identityCellPad = isFeed ? "pl-2 pr-4 py-4" : cellPad;
  const desktopAvatarSize = avatarSize ?? (isFeed ? "md" : "lg");
  const mobileAvatarSize = avatarSize ?? "md";
  let t4;
  if ($[0] !== className || $[1] !== size || $[2] !== user || $[3] !== variant) {
    t4 = avSize => <UserIdentity user={user} variant={variant} size={size} avatarSize={avSize} className={cn("min-w-0", className)} handleClassName="text-foreground-tertiary" nameClassName="text-body" showProviderBadges={true} />;
    $[0] = className;
    $[1] = size;
    $[2] = user;
    $[3] = variant;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const renderIdentity = t4;
  if (layout === "desktop") {
    let t5;
    if ($[5] !== identityCellPad || $[6] !== isFeed) {
      t5 = cn(identityCellPad, identityColClass(isFeed));
      $[5] = identityCellPad;
      $[6] = isFeed;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    const t6 = isFeed ? "gap-8" : "gap-6";
    let t7;
    if ($[8] !== t6) {
      t7 = cn("flex min-w-0 items-center", t6);
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== desktopAvatarSize || $[11] !== renderIdentity) {
      t8 = renderIdentity(desktopAvatarSize);
      $[10] = desktopAvatarSize;
      $[11] = renderIdentity;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== showYouBadge || $[14] !== youLabel) {
      t9 = showYouBadge === true && <Badge variant="secondary" className="shrink-0 text-micro">{youLabel}</Badge>;
      $[13] = showYouBadge;
      $[14] = youLabel;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] !== badge || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
      t10 = <div className={t7}>{t8}{t9}{badge}</div>;
      $[16] = badge;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== t10 || $[22] !== t5) {
      t11 = <TableCell className={t5}>{t10}</TableCell>;
      $[21] = t10;
      $[22] = t5;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  let t5;
  if ($[24] !== mobileAvatarSize || $[25] !== renderIdentity) {
    t5 = renderIdentity(mobileAvatarSize);
    $[24] = mobileAvatarSize;
    $[25] = renderIdentity;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== showYouBadge || $[28] !== youLabel) {
    t6 = showYouBadge === true && <Badge variant="secondary" className="shrink-0 text-micro">{youLabel}</Badge>;
    $[27] = showYouBadge;
    $[28] = youLabel;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== badge || $[31] !== t5 || $[32] !== t6) {
    t7 = <div className="flex min-w-0 flex-1 items-center gap-2">{t5}{t6}{badge}</div>;
    $[30] = badge;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
}
DataTableIdentityCell.displayName = 'DataTableIdentityCell';

// ---------------------------------------------------------------------------
// DataTable.NumericCell
// ---------------------------------------------------------------------------

export type NumericFormat = 'usd' | 'usdSigned' | 'usdAdaptive' | 'usdAdaptiveSigned' | 'int' | 'compact' | 'compactSigned';
export type NumericTone = 'auto' | 'positive' | 'negative' | 'muted' | 'default';
export interface NumericCellProps {
  /** Numeric value or D6-encoded string. */
  value: number | string;
  /** Formatter. */
  format: NumericFormat;
  /** Color tone. Default 'default'. */
  tone?: NumericTone;
  /** Use Geist Mono. Default false. Both variants set tabular-nums. */
  mono?: boolean;
  /** Animate value changes with AnimatedNumber. Default TRUE. */
  animated?: boolean;
  /** Show threshold-icon (CircleArrowDown02IconSolid) rotated by sign. Default false. */
  showThresholdIcon?: boolean;
  /** Cell alignment. Default 'right'. */
  align?: 'left' | 'right';
  /** AnimatedNumber duration override. Default 800ms. */
  animationDuration?: number;
  /** Skip in mobile card layout. */
  hideOnMobile?: boolean;
  /**
   * Optional muted value rendered BELOW the primary value in the mobile card
   * layout (right-aligned stack). Lets a table fold a secondary metric (e.g.
   * boost count, relative date) under its primary money column instead of
   * spending a second horizontal cell that crushes the identity. Ignored on
   * desktop.
   */
  mobileSecondary?: ReactNode;
  /** Caption rendered above the value in the mobile card layout only. */
  mobileLabel?: string;
  className?: string;
}
type CurrencyFormatters = ReturnType<typeof useFormattedCurrency>;
interface NumericFormatContext {
  formatD6: CurrencyFormatters['formatD6'];
  formatD6Signed: CurrencyFormatters['formatD6Signed'];
  formatBalance: CurrencyFormatters['formatBalance'];
  formatInteger: ReturnType<typeof formatCountFor>;
}
const SIGNED_NUMERIC_FORMATS: ReadonlySet<NumericFormat> = new Set(['usdSigned', 'usdAdaptiveSigned', 'compactSigned']);
function DataTableNumericCell(t0) {
  const $ = _c(52);
  const {
    value,
    format,
    tone: t1,
    mono: t2,
    animated: t3,
    showThresholdIcon: t4,
    align: t5,
    animationDuration,
    hideOnMobile,
    mobileSecondary,
    mobileLabel,
    className
  } = t0;
  const tone = t1 === undefined ? "default" : t1;
  const mono = t2 === undefined ? false : t2;
  const animated = t3 === undefined ? true : t3;
  const showThresholdIcon = t4 === undefined ? false : t4;
  const align = t5 === undefined ? "right" : t5;
  const layout = useLayout();
  const cellPad = useDesktopCellPad();
  const {
    formatD6,
    formatD6Signed,
    formatBalance
  } = useFormattedCurrency();
  const t6 = useLocale();
  let t7;
  if ($[0] !== t6) {
    t7 = formatCountFor(t6);
    $[0] = t6;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const formatInteger = t7;
  if (hideOnMobile === true && layout === "mobile") {
    return null;
  }
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t8;
  let t9;
  if ($[2] !== animated || $[3] !== animationDuration || $[4] !== format || $[5] !== formatBalance || $[6] !== formatD6 || $[7] !== formatD6Signed || $[8] !== formatInteger || $[9] !== mono || $[10] !== showThresholdIcon || $[11] !== tone || $[12] !== value) {
    const safeValue = normalizeNumericValue(value);
    let t15;
    if ($[21] !== format || $[22] !== showThresholdIcon || $[23] !== tone) {
      t15 = shouldStripNumericSign(format, showThresholdIcon, tone);
      $[21] = format;
      $[22] = showThresholdIcon;
      $[23] = tone;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    const stripSignPrefix = t15;
    let t16;
    if ($[25] !== formatBalance || $[26] !== formatD6 || $[27] !== formatD6Signed || $[28] !== formatInteger) {
      t16 = {
        formatD6,
        formatD6Signed,
        formatBalance,
        formatInteger
      };
      $[25] = formatBalance;
      $[26] = formatD6;
      $[27] = formatD6Signed;
      $[28] = formatInteger;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    const formatContext = t16;
    let t17;
    if ($[30] !== format || $[31] !== formatContext || $[32] !== stripSignPrefix) {
      t17 = nextValue => formatNumericValue(nextValue, format, stripSignPrefix, formatContext);
      $[30] = format;
      $[31] = formatContext;
      $[32] = stripSignPrefix;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    const applyFormat = t17;
    const showIcon = showThresholdIcon === true && Math.abs(safeValue) >= PNL_DUST_D6;
    T0 = NumericCellContent;
    t8 = safeValue;
    t9 = applyFormat;
    t10 = animated;
    t11 = animationDuration;
    t12 = showIcon;
    t13 = mono;
    t14 = numericToneClassName(tone, safeValue);
    $[2] = animated;
    $[3] = animationDuration;
    $[4] = format;
    $[5] = formatBalance;
    $[6] = formatD6;
    $[7] = formatD6Signed;
    $[8] = formatInteger;
    $[9] = mono;
    $[10] = showThresholdIcon;
    $[11] = tone;
    $[12] = value;
    $[13] = T0;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
    t14 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t15;
  if ($[34] !== T0 || $[35] !== align || $[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t8 || $[42] !== t9) {
    t15 = <T0 value={t8} format={t9} animated={t10} animationDuration={t11} showIcon={t12} mono={t13} toneClassName={t14} align={align} />;
    $[34] = T0;
    $[35] = align;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t8;
    $[42] = t9;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const content = t15;
  let t16;
  if ($[44] !== align || $[45] !== cellPad || $[46] !== className || $[47] !== content || $[48] !== layout || $[49] !== mobileLabel || $[50] !== mobileSecondary) {
    t16 = <NumericCellLayout layout={layout} cellPad={cellPad} align={align} mobileSecondary={mobileSecondary} mobileLabel={mobileLabel} className={className}>{content}</NumericCellLayout>;
    $[44] = align;
    $[45] = cellPad;
    $[46] = className;
    $[47] = content;
    $[48] = layout;
    $[49] = mobileLabel;
    $[50] = mobileSecondary;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  return t16;
}
DataTableNumericCell.displayName = 'DataTableNumericCell';
function normalizeNumericValue(value: number | string): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}
function shouldStripNumericSign(format: NumericFormat, showThresholdIcon: boolean, tone: NumericTone): boolean {
  return SIGNED_NUMERIC_FORMATS.has(format) && (showThresholdIcon || tone === 'auto');
}
function formatNumericValue(value: number, format: NumericFormat, stripSignPrefix: boolean, context: NumericFormatContext): string {
  const safeValue = Number.isFinite(value) ? value : 0;
  const roundedValue = String(Math.round(safeValue));
  const roundedAbsoluteValue = String(Math.round(Math.abs(safeValue)));
  switch (format) {
    case 'usd':
      return context.formatD6(roundedValue);
    case 'usdSigned':
      return stripSignPrefix ? context.formatD6(roundedAbsoluteValue) : context.formatD6Signed(roundedValue);
    case 'usdAdaptive':
      return context.formatD6(roundedValue, 'adaptive');
    case 'usdAdaptiveSigned':
      return stripSignPrefix ? context.formatD6(roundedAbsoluteValue, 'adaptive') : context.formatD6Signed(roundedValue, 'adaptive');
    case 'int':
      return context.formatInteger(safeValue);
    case 'compactSigned':
      return formatCompactSigned(safeValue, stripSignPrefix, context.formatBalance);
    case 'compact':
      return context.formatBalance(safeValue / D6_SCALE, 'compactTight');
  }
}
function formatCompactSigned(value: number, stripSignPrefix: boolean, formatBalance: NumericFormatContext['formatBalance']): string {
  const absoluteValue = formatBalance(Math.abs(value) / D6_SCALE, 'compactTight');
  if (stripSignPrefix || value === 0) return absoluteValue;
  return value > 0 ? `+${absoluteValue}` : `-${absoluteValue}`;
}
function numericToneClassName(tone: NumericTone, value: number): string {
  switch (tone) {
    case 'auto':
      return pnlColorClass(value);
    case 'positive':
      return 'text-feedback-success-content';
    case 'negative':
      return 'text-feedback-error-content';
    case 'muted':
      return 'text-foreground-tertiary';
    case 'default':
      return 'text-foreground-secondary';
  }
}
interface NumericCellContentProps {
  value: number;
  format: (value: number) => string;
  animated: boolean;
  animationDuration: number | undefined;
  showIcon: boolean;
  mono: boolean;
  toneClassName: string;
  align: 'left' | 'right';
}
function NumericCellContent(t0) {
  const $ = _c(18);
  const {
    value,
    format,
    animated,
    animationDuration,
    showIcon,
    mono,
    toneClassName,
    align
  } = t0;
  let t1;
  if ($[0] !== animated || $[1] !== animationDuration || $[2] !== format || $[3] !== value) {
    t1 = animated ? <AnimatedNumber value={value} format={format} duration={animationDuration ?? 800} className="tabular-nums" /> : <span className="font-sans tabular-nums">{format(value)}</span>;
    $[0] = animated;
    $[1] = animationDuration;
    $[2] = format;
    $[3] = value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const valueDisplay = t1;
  const t2 = mono && "font-mono";
  const t3 = align === "left" ? "justify-start" : "justify-end";
  let t4;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== toneClassName) {
    t4 = cn("inline-flex items-center gap-1 font-sans text-body tabular-nums whitespace-nowrap", t2, toneClassName, t3);
    $[5] = t2;
    $[6] = t3;
    $[7] = toneClassName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== showIcon || $[10] !== value) {
    t5 = showIcon ? <NumericThresholdIcon key={numericSign(value)} value={value} /> : null;
    $[9] = showIcon;
    $[10] = value;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <AnimatePresence initial={false} mode="wait">{t5}</AnimatePresence>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6 || $[16] !== valueDisplay) {
    t7 = <span className={t4}>{t6}{valueDisplay}</span>;
    $[14] = t4;
    $[15] = t6;
    $[16] = valueDisplay;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function NumericThresholdIcon(t0) {
  const $ = _c(8);
  const {
    value
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      scale: 0.8
    };
    t2 = {
      opacity: 1,
      scale: 1
    };
    t3 = {
      opacity: 0,
      scale: 0.8
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  const t4 = value > 0 ? 180 : 0;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      rotate: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={CircleArrowDown02IconSolid} className="size-3" />;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t5) {
    t7 = <m.span initial={t1} animate={t2} exit={t3} transition={MOTION_POSITION_SMALL} className="inline-flex size-3 shrink-0 items-center justify-center"><m.span initial={false} animate={t5} transition={MOTION_POSITION_SMALL} className="inline-flex size-3 items-center justify-center">{t6}</m.span></m.span>;
    $[6] = t5;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}
function numericSign(value: number): number {
  if (value > 0) return 1;
  if (value < 0) return -1;
  return 0;
}
interface NumericCellLayoutProps {
  layout: Layout;
  cellPad: string;
  align: 'left' | 'right';
  mobileSecondary: ReactNode;
  mobileLabel: string | undefined;
  className: string | undefined;
  children: ReactNode;
}
function NumericCellLayout(t0) {
  const $ = _c(33);
  const {
    layout,
    cellPad,
    align,
    mobileSecondary,
    mobileLabel,
    className,
    children
  } = t0;
  if (layout === "desktop") {
    const t1 = align === "right" ? "text-right" : "text-left";
    let t2;
    if ($[0] !== cellPad || $[1] !== className || $[2] !== t1) {
      t2 = cn(cellPad, "text-body", t1, className);
      $[0] = cellPad;
      $[1] = className;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== children || $[5] !== t2) {
      t3 = <TableCell className={t2}>{children}</TableCell>;
      $[4] = children;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (mobileLabel !== undefined) {
    let t1;
    if ($[7] !== className) {
      t1 = cn("flex w-full min-w-0 flex-col items-start gap-0.5", className);
      $[7] = className;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== mobileLabel) {
      t2 = <span className="w-full truncate text-caption font-normal text-foreground-secondary">{mobileLabel}</span>;
      $[9] = mobileLabel;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    let t3;
    if ($[11] !== children || $[12] !== mobileSecondary) {
      t3 = mobileSecondary !== undefined ? <><span className="min-w-0 text-body font-medium">{children}</span><span className="text-caption tabular-nums text-foreground-tertiary">{mobileSecondary}</span></> : children;
      $[11] = children;
      $[12] = mobileSecondary;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
      t4 = <span className={t1}>{t2}{t3}</span>;
      $[14] = t1;
      $[15] = t2;
      $[16] = t3;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    return t4;
  }
  if (mobileSecondary !== undefined) {
    let t1;
    if ($[18] !== className) {
      t1 = cn("ml-auto flex shrink-0 flex-col items-end gap-0.5", className);
      $[18] = className;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    let t2;
    if ($[20] !== children) {
      t2 = <span className="text-body font-medium">{children}</span>;
      $[20] = children;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    let t3;
    if ($[22] !== mobileSecondary) {
      t3 = <span className="text-caption tabular-nums text-foreground-tertiary">{mobileSecondary}</span>;
      $[22] = mobileSecondary;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    let t4;
    if ($[24] !== t1 || $[25] !== t2 || $[26] !== t3) {
      t4 = <span className={t1}>{t2}{t3}</span>;
      $[24] = t1;
      $[25] = t2;
      $[26] = t3;
      $[27] = t4;
    } else {
      t4 = $[27];
    }
    return t4;
  }
  let t1;
  if ($[28] !== className) {
    t1 = cn("ml-auto shrink-0 text-body font-medium", className);
    $[28] = className;
    $[29] = t1;
  } else {
    t1 = $[29];
  }
  let t2;
  if ($[30] !== children || $[31] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[30] = children;
    $[31] = t1;
    $[32] = t2;
  } else {
    t2 = $[32];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// DataTable.ClickableCell — wraps content in Link + optional AppTooltip
// ---------------------------------------------------------------------------

export interface ClickableCellProps {
  /** Link href. Forwarded to next-intl's <Link>. */
  href: ComponentProps<typeof Link>['href'];
  /** Optional tooltip shown on hover. */
  tooltip?: string;
  /** Required aria-label for the link. */
  ariaLabel: string;
  /** Cell alignment. Default 'right'. */
  align?: 'left' | 'right';
  /** Skip in mobile card layout. */
  hideOnMobile?: boolean;
  children: ReactNode;
  className?: string;
}
function DataTableClickableCell(t0) {
  const $ = _c(22);
  const {
    href,
    tooltip,
    ariaLabel,
    align: t1,
    hideOnMobile,
    children,
    className
  } = t0;
  const align = t1 === undefined ? "right" : t1;
  const layout = useLayout();
  const cellPad = useDesktopCellPad();
  if (hideOnMobile === true && layout === "mobile") {
    return null;
  }
  const t2 = align === "right" ? "justify-end" : "justify-start";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("inline-flex items-center gap-1.5 rounded-control px-1.5 py-0.5 transition-colors", "hover:bg-background-tertiary hover:text-foreground-primary", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <span className="tabular-nums text-foreground-secondary group-hover:text-foreground-primary">{children}</span>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={ArrowUpRight01Icon} className="size-3.5 text-foreground-tertiary" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== ariaLabel || $[7] !== href || $[8] !== t3 || $[9] !== t4) {
    t6 = <Link href={href} aria-label={ariaLabel} onClick={_temp} className={t3}>{t4}{t5}</Link>;
    $[6] = ariaLabel;
    $[7] = href;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const link = t6;
  let t7;
  if ($[11] !== link || $[12] !== tooltip) {
    t7 = tooltip !== undefined ? <AppTooltip content={tooltip} delayDuration={0}>{link}</AppTooltip> : link;
    $[11] = link;
    $[12] = tooltip;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const wrapped = t7;
  if (layout === "desktop") {
    const t8 = align === "right" ? "text-right" : "text-left";
    let t9;
    if ($[14] !== cellPad || $[15] !== t8) {
      t9 = cn(cellPad, t8);
      $[14] = cellPad;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== t9 || $[18] !== wrapped) {
      t10 = <TableCell className={t9}>{wrapped}</TableCell>;
      $[17] = t9;
      $[18] = wrapped;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    return t10;
  }
  let t8;
  if ($[20] !== wrapped) {
    t8 = <span className="ml-auto shrink-0">{wrapped}</span>;
    $[20] = wrapped;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}
function _temp(e) {
  e.stopPropagation();
}
DataTableClickableCell.displayName = 'DataTableClickableCell';

// ---------------------------------------------------------------------------
// DataTable.TextCell — free-form right-aligned text cell (e.g. relative dates).
//
// Use when a column value is non-numeric and non-clickable but should still
// inherit the column's alignment + tabular-nums chrome (e.g. "2h ago" date
// strings on the market-detail Boosters table).
// ---------------------------------------------------------------------------

export interface TextCellProps {
  children: ReactNode;
  align?: 'left' | 'right';
  hideOnMobile?: boolean;
  className?: string;
  /** Apply tabular-nums (default true — keeps date strings vertically aligned). */
  tabularNums?: boolean;
  /** Color tone — default 'muted' (text-foreground-secondary). */
  tone?: 'default' | 'muted' | 'tertiary';
  /** Caption rendered above the value in the mobile card layout only. */
  mobileLabel?: string;
}
function DataTableTextCell(t0) {
  const $ = _c(19);
  const {
    children,
    align: t1,
    hideOnMobile,
    className,
    tabularNums: t2,
    tone: t3,
    mobileLabel
  } = t0;
  const align = t1 === undefined ? "right" : t1;
  const tabularNums = t2 === undefined ? true : t2;
  const tone = t3 === undefined ? "muted" : t3;
  const layout = useLayout();
  const cellPad = useDesktopCellPad();
  if (hideOnMobile === true && layout === "mobile") {
    return null;
  }
  const toneClass = tone === "default" ? "text-foreground-primary" : tone === "tertiary" ? "text-foreground-tertiary" : "text-foreground-secondary";
  let t4;
  let t5;
  if ($[0] !== align || $[1] !== cellPad || $[2] !== children || $[3] !== className || $[4] !== layout || $[5] !== mobileLabel || $[6] !== tabularNums || $[7] !== toneClass) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const contentClass = cn(tabularNums === true && "tabular-nums", "text-body whitespace-nowrap", toneClass);
      if (layout === "desktop") {
        const t6 = align === "right" ? "text-right" : "text-left";
        let t7;
        if ($[10] !== cellPad || $[11] !== className || $[12] !== t6) {
          t7 = cn(cellPad, t6, className);
          $[10] = cellPad;
          $[11] = className;
          $[12] = t6;
          $[13] = t7;
        } else {
          t7 = $[13];
        }
        t5 = <TableCell className={t7}><span className={contentClass}>{children}</span></TableCell>;
        break bb0;
      }
      if (mobileLabel !== undefined) {
        let t6;
        if ($[14] !== mobileLabel) {
          t6 = <span className="text-caption font-normal text-foreground-secondary">{mobileLabel}</span>;
          $[14] = mobileLabel;
          $[15] = t6;
        } else {
          t6 = $[15];
        }
        t5 = <span className={cn("flex min-w-0 flex-col items-start gap-0.5", contentClass, className)}>{t6}{children}</span>;
        break bb0;
      }
      t4 = cn("ml-auto shrink-0", contentClass, className);
    }
    $[0] = align;
    $[1] = cellPad;
    $[2] = children;
    $[3] = className;
    $[4] = layout;
    $[5] = mobileLabel;
    $[6] = tabularNums;
    $[7] = toneClass;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[16] !== children || $[17] !== t4) {
    t6 = <span className={t4}>{children}</span>;
    $[16] = children;
    $[17] = t4;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
DataTableTextCell.displayName = 'DataTableTextCell';

// ---------------------------------------------------------------------------
// Compound assembly + EntriesContext wiring
//
// The root must provide the EntriesContext so DataTable.Body can read entries.
// We do this by wrapping the root's children in EntriesContext.Provider
// (the inner section already sets SortContext + LayoutContext).
//
// For type-safety we re-export the root as the entry point with the generics.
// ---------------------------------------------------------------------------

function DataTableRootWithEntriesProvider(props) {
  const $ = _c(5);
  const t0 = props.entries as ReadonlyArray<unknown>;
  let t1;
  if ($[0] !== props) {
    t1 = <DataTableRoot {...props} />;
    $[0] = props;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <EntriesContext.Provider value={t0}>{t1}</EntriesContext.Provider>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type DataTableComponent = (<TEntry, TSort extends string>(props: DataTableProps<TEntry, TSort>) => React.ReactNode) & {
  Header: typeof DataTableHeader;
  RankColumn: typeof DataTableRankColumn;
  IdentityColumn: typeof DataTableIdentityColumn;
  SortableColumn: typeof DataTableSortableColumn;
  Column: typeof DataTableColumn;
  Body: typeof DataTableBody;
  Row: typeof DataTableRow;
  RankCell: typeof DataTableRankCell;
  IdentityCell: typeof DataTableIdentityCell;
  NumericCell: typeof DataTableNumericCell;
  ClickableCell: typeof DataTableClickableCell;
  TextCell: typeof DataTableTextCell;
};
const DataTable = DataTableRootWithEntriesProvider as DataTableComponent;
DataTable.Header = DataTableHeader;
DataTable.RankColumn = DataTableRankColumn;
DataTable.IdentityColumn = DataTableIdentityColumn;
DataTable.SortableColumn = DataTableSortableColumn;
DataTable.Column = DataTableColumn;
DataTable.Body = DataTableBody;
DataTable.Row = DataTableRow;
DataTable.RankCell = DataTableRankCell;
DataTable.IdentityCell = DataTableIdentityCell;
DataTable.NumericCell = DataTableNumericCell;
DataTable.ClickableCell = DataTableClickableCell;
DataTable.TextCell = DataTableTextCell;
export { DataTable };

// Named sub-component exports for consumers preferring flat imports.
export { DataTableHeader, DataTableRankColumn, DataTableIdentityColumn, DataTableSortableColumn, DataTableColumn, DataTableBody, DataTableRow, DataTableRankCell, DataTableIdentityCell, DataTableNumericCell, DataTableClickableCell, DataTableTextCell };